/**
 * The follow band's underline draw.
 *
 * This file does one thing: hold the underline back so it can draw itself the first time the band
 * scrolls into view. Everything else about the band, both panels, all three rules, the four links
 * and the underline itself, is markup and CSS, and the underline's resting state is fully drawn. So
 * this module is the only thing that can make the line animate and nothing that can make it vanish:
 * blocked, failed or unsupported, the band is the finished design.
 *
 * That inversion is why there is no geometry in here. Zap's version rebuilds the path `d` in JS on
 * every resize, scaling a hard coded squiggle by `width / 172`, and it measures the text to fit it to
 * one line. Both jobs moved into the SVG and the stylesheet: `preserveAspectRatio="none"` stretches
 * the path to the word, and `pathLength="1"` makes the line measure 1 whatever it renders at, so a
 * dash of 1 covers it and an offset of 1 hides it. Nothing to recompute, so there is no resize
 * handler and no measuring pass.
 *
 * What is left is one ordering problem. Setting the undrawn state the moment this module runs is
 * wrong for a band that is already on screen: module scripts execute after the first paint, so the
 * reader would watch a drawn line blink out and redraw itself under the cursor. Asking
 * `getBoundingClientRect` whether the band is below the fold is also wrong, and measurably so: this
 * module runs before images above the band have loaded, so on a page whose upper sections were still
 * settling the band read as on screen at upgrade time and 898px down the page a moment later.
 *
 * So the decision is handed to the observer, which is the only thing that knows the answer at the
 * right moment and revises it when the page reflows. Its first observation arms the effect instead of
 * triggering it: not intersecting means the band is safely off screen and can be set undrawn with
 * nothing to see, intersecting means the reader is already looking at it and it is left alone for
 * good. Only an intersection after arming draws the line, and the observer is disconnected on the way
 * out so scrolling back never redraws it.
 */

class NastyFollow extends HTMLElement {
  #observer;

  connectedCallback() {
    /* The stylesheet already forces the drawn state under reduced motion; do not fight it. */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    let armed = false;

    this.#observer = new IntersectionObserver((entries) => {
      const onScreen = entries[entries.length - 1].isIntersecting;

      if (!armed) {
        /* Already in view on the first look: leave the line drawn rather than redraw it. */
        if (onScreen) {
          this.#observer.disconnect();
          return;
        }
        armed = true;
        this.dataset.draw = 'pending';
        return;
      }

      if (!onScreen) return;
      this.#observer.disconnect();
      this.dataset.draw = 'drawn';
    });

    this.#observer.observe(this);
  }

  /* Section reload in the editor moves the element, so the observer goes with it. */
  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

if (!customElements.get('nasty-follow')) {
  customElements.define('nasty-follow', NastyFollow);
}
